import { useEffect, useState } from 'react';

export interface ScriptOptions {
  src: string;
  id?: string;
  loadedName: string;
  /** Useful for environments where we don't want to load the script. */
  shouldLoad?: boolean;
}
export type Status = 'idle' | 'loading' | 'ready' | 'error';
export type ScriptElt = HTMLScriptElement | null;

export const loadScript = <T>(name: string): Promise<T | null> =>
  new Promise((resolve) => {
    let attempts = 0;

    (function checkWindow(): void | NodeJS.Timeout {
      attempts += 1;
      if (window[name as any]) {
        return resolve(window[name as any] as T);
      }
      if (attempts > 100) {
        return resolve(null);
      }
      return setTimeout(() => {
        checkWindow();
      }, 100);
    })();
  });

export const useScript = ({ id, loadedName, shouldLoad = true, src }: ScriptOptions): Status => {
  const [status, setStatus] = useState<Status>(src ? 'loading' : 'idle');

  useEffect(() => {
    if (!src || !shouldLoad) {
      setStatus('idle');
      return (): string => status;
    }

    let script: ScriptElt = document.querySelector(`script[src="${src}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = src;
      script.id = id || '';
      script.async = true;
      script.defer = true;
      script.type = 'text/javascript';
      script.setAttribute('data-status', 'loading');
      document.body.appendChild(script);

      const setAttributeFromEvent = async (event: Event): Promise<void> => {
        if (event.type === 'load') {
          await loadScript(loadedName);
          script?.setAttribute('data-status', 'ready');
        } else {
          script?.setAttribute('data-status', 'error');
        }
      };
      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      setStatus(script.getAttribute('data-status') as Status);
    }

    const setStateFromEvent = async (event: Event): Promise<void> => {
      if (event.type === 'load') {
        await loadScript(loadedName);
        setStatus('ready');
      } else {
        setStatus('error');
      }
    };
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);

    return (): void => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src, id, status, loadedName, shouldLoad]);

  return status;
};
